import { Empty } from "antd";
import {
  IoBookmark,
  IoChatbubbleEllipses,
  IoHeartSharp,
  IoPlay,
  IoShareSocialSharp,
  IoVolumeMediumSharp,
  IoVolumeMuteSharp,
} from "react-icons/io5";
import { formatCnt } from "../../../utils/formatCnt";
import { useRef, useState } from "react";
import test_img from "../../../../images/32637972_7843794.jpg";

export const TiktokContents = ({ tiktok }) => {
  const [hoveredVideoId, setHoveredVideoId] = useState(null);
  const [isMuted, setIsMuted] = useState(true);
  const videoRefs = useRef({});

  const videos = tiktok || [];

  const handleOpenVideo = (videoUrl) => {
    if (!videoUrl) return;
    window.open(videoUrl, "_blank", "noopener,noreferrer");
  };

  const handleVideoMouseEnter = (video) => {
    setHoveredVideoId(video.video_id);

    const videoElement = videoRefs.current[video.video_id];
    if (!videoElement) return;

    videoElement.play().catch(() => {
      // 브라우저 autoplay 정책에 막히면 무음 상태 유지
    });
  };

  const handleVideoMouseLeave = (videoId) => {
    setHoveredVideoId(null);

    const videoElement = videoRefs.current[videoId];
    if (!videoElement) return;

    videoElement.pause();
  };

  const handleToggleMute = (e, videoId) => {
    e.stopPropagation();

    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
  };

  const getDisplayHashtags = (hashtagName) => {
    if (!hashtagName) return null;

    const hashtags = hashtagName
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean)
      .slice(0, 5);

    return hashtags.length > 0 ? hashtags.join(" ") : null;
  };

  return (
    <>
      {videos.length > 0 ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {videos.map((video) => (
              <div
                key={video.video_id}
                className="group overflow-hidden rounded-[24px] border border-[#ebeff5] text-left shadow-[0_8px_24px_rgba(15,23,42,0.06)] transition-shadow duration-300 hover:shadow-[0_18px_40px_rgba(15,23,42,0.12)]"
                onMouseEnter={() => handleVideoMouseEnter(video)}
                onMouseLeave={() => handleVideoMouseLeave(video.video_id)}
                onClick={() => handleOpenVideo(video.video)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleOpenVideo(video.video);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={video.thumbnail || test_img}
                    alt="브랜드 영상 썸네일"
                    className={`absolute inset-0 scale-[1.01] h-full w-full object-cover transition-all duration-300 ${
                      hoveredVideoId === video.video_id
                        ? "scale-[1.03] opacity-0"
                        : "scale-100 opacity-100"
                    }`}
                    onError={(e) => {
                      e.currentTarget.src = test_img;
                    }}
                  />
                  {video.video ? (
                    <video
                      ref={(element) => {
                        if (element) {
                          videoRefs.current[video.video_id] = element;
                        }
                      }}
                      src={video.video}
                      poster={video.thumbnail || test_img}
                      className={`absolute inset-0 scale-[1.01] h-full w-full object-cover rounded-[inherit] will-change-transform transition-opacity duration-300 ${
                        hoveredVideoId === video.video_id
                          ? "opacity-100"
                          : "opacity-0"
                      }`}
                      muted={isMuted}
                      loop
                      playsInline
                      preload="metadata"
                    />
                  ) : null}
                  {hoveredVideoId === video.video_id && video.video ? (
                    <button
                      type="button"
                      className="absolute right-3 top-3 z-10 inline-flex h-9 w-9 md:h-7 md:w-7 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition hover:bg-black/70"
                      onClick={(e) => handleToggleMute(e, video.video_id)}
                      aria-label={isMuted ? "소리 켜기" : "소리 끄기"}
                    >
                      {isMuted ? (
                        <IoVolumeMuteSharp className="text-[16px] md:text-[12px]" />
                      ) : (
                        <IoVolumeMediumSharp className="text-[16px] md:text-[12px]" />
                      )}
                    </button>
                  ) : null}
                </div>
                <div className="text-[14px] md:text-[12px] text-[#595959] space-y-1 p-4">
                  <p className="flex items-center gap-1.5 m-0 truncate">
                    <IoPlay className="text-[#13C2C2]" />
                    <span>{formatCnt(video.play_count || 0)}</span>
                  </p>
                  <p className="flex items-center gap-1.5 m-0 truncate">
                    <IoHeartSharp className="text-[#FF4D4F]" />
                    <span>{formatCnt(video.like_count || 0)}</span>
                  </p>
                  <p className="flex items-center gap-1.5 m-0 truncate">
                    <IoChatbubbleEllipses className="text-[#1890FF]" />
                    <span>{formatCnt(video.comment_count || 0)}</span>
                  </p>
                  <p className="flex items-center gap-1.5 m-0 truncate">
                    <IoShareSocialSharp className="text-[#52C41A]" />
                    <span>{formatCnt(video.share_count || 0)}</span>
                  </p>
                  <p className="flex items-center gap-1.5 m-0 truncate">
                    <IoBookmark className="text-[#FAAD14]" />
                    <span>{formatCnt(video.collect_count || 0)}</span>
                  </p>
                  <p className="text-[14px] md:text-[12px] mt-[6px] md:mt-[8px] mb-0 text-[#1890FF] overflow-hidden text-ellipsis whitespace-nowrap font-medium min-h-[16px] md:min-h-[20px]">
                    {getDisplayHashtags(video.hashtag_name)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <Empty
          description={
            <div className="text-[14px] text-[#666]">
              브랜드 영상이 없습니다.
            </div>
          }
        />
      )}
    </>
  );
};
